import { useState } from "react";
import { Sunrise, Briefcase, Utensils, X, Wand2 } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const FIELDS = [
  { key: "wake_time", label: "When do you wake up?", icon: Sunrise, default: "07:00" },
  { key: "work_start", label: "When does your work/focus day start?", icon: Briefcase, default: "09:00" },
  { key: "work_end", label: "When does it wrap up?", icon: Briefcase, default: "17:00" },
  { key: "dinner_time", label: "When is dinner usually?", icon: Utensils, default: "18:00" },
];

const WEEKEND_FIELDS = [
  { key: "weekend_wake", label: "Weekend wake-up (slower is fine)", icon: Sunrise, default: "08:00" },
  { key: "weekend_dinner", label: "Weekend dinner time", icon: Utensils, default: "18:00" },
];

export const RhythmSetupModal = ({ token, onClose, onDone, palette }) => {
  const [times, setTimes] = useState(
    Object.fromEntries([...FIELDS, ...WEEKEND_FIELDS].map((f) => [f.key, f.default]))
  );
  const [saving, setSaving] = useState(false);

  const build = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/rhythm/setup`, times, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Your rhythm is set — weekday & weekend timelines rebuilt 🌿");
      onDone();
      onClose();
    } catch (e) {
      toast.error("Couldn't build your day — check the times");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="rhythm-setup-modal"
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-xl font-bold font-body" style={{ color: palette.primary }}>Set Up My Day</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5" aria-label="Close" data-testid="setup-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs opacity-70 mb-5">
          A few quick times and we'll rebuild your weekday and weekend timelines around your real rhythm. This replaces the current templates — your check-ins and streak are untouched.
        </p>
        <div className="space-y-4">
          {FIELDS.map(({ key, label, icon: Icon }) => (
            <div key={key} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${palette.primary}15` }}>
                <Icon className="w-4 h-4" style={{ color: palette.primary }} />
              </div>
              <label className="flex-1 text-sm font-medium">{label}</label>
              <input
                type="time"
                className="rounded-xl border px-3 py-2 text-sm"
                style={{ borderColor: palette.border }}
                value={times[key]}
                onChange={(e) => setTimes((p) => ({ ...p, [key]: e.target.value }))}
                data-testid={`setup-${key}`}
              />
            </div>
          ))}
          <p className="text-xs font-bold uppercase tracking-widest pt-2" style={{ color: "#D97706" }}>🌿 Weekend rhythm</p>
          {WEEKEND_FIELDS.map(({ key, label, icon: Icon }) => (
            <div key={key} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#D9770618" }}>
                <Icon className="w-4 h-4" style={{ color: "#D97706" }} />
              </div>
              <label className="flex-1 text-sm font-medium">{label}</label>
              <input
                type="time"
                className="rounded-xl border px-3 py-2 text-sm"
                style={{ borderColor: palette.border }}
                value={times[key]}
                onChange={(e) => setTimes((p) => ({ ...p, [key]: e.target.value }))}
                data-testid={`setup-${key}`}
              />
            </div>
          ))}
        </div>
        <button
          onClick={build}
          disabled={saving}
          className="w-full mt-6 rounded-xl py-3 text-sm font-bold text-white flex items-center justify-center gap-2"
          style={{ backgroundColor: palette.primary, opacity: saving ? 0.6 : 1 }}
          data-testid="setup-build-btn"
        >
          <Wand2 className="w-4 h-4" />
          Build My Day
        </button>
      </div>
    </div>
  );
};
